import merge from "lodash-es/merge"
import { useEffect, useMemo, useState } from "react"

// utils
import localStorageAvailable from "@sap/utils/localStorageAvailable"

// ----------------------------------------------------------------------

export default function useLocalStorage(key: string, defaultValue: unknown) {
  const storageAvailable = useMemo(() => localStorageAvailable(), [])

  const [value, setValue] = useState(() => {
    const storedValue = storageAvailable ? localStorage.getItem(key) : null

    let value = storedValue === null ? defaultValue : JSON.parse(storedValue)
    if (typeof defaultValue === "object") {
      value = merge(defaultValue, value)
    }
    return value
  })

  useEffect(() => {
    const listener = (e: StorageEvent) => {
      if (e.storageArea === localStorage && e.key === key) {
        setValue(e.newValue ? JSON.parse(e.newValue) : e.newValue)
      }
    }
    window.addEventListener("storage", listener)

    return () => {
      window.removeEventListener("storage", listener)
    }
  }, [key, defaultValue])

  const setValueInLocalStorage = (newValue: unknown) => {
    setValue((currentValue: unknown) => {
      const result = typeof newValue === "function" ? newValue(currentValue) : newValue

      if (storageAvailable) {
        localStorage.setItem(key, JSON.stringify(result))
      }

      return result
    })
  }

  return [value, setValueInLocalStorage] as const
}
